import React, { useState, useEffect } from 'react';
import {
  Typography,
  Stack,
  Paper,
  Divider,
  IconButton,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Wrapper, CustomTable, Loader } from "../../components";
import { DataColumns } from "./constant";
import { generateApiKeyAndToken } from "../../services";
import {
  keysPair,
  deleteKeysPair,
  regenerateKeys,
} from "../../services/keysPairService";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useTheme } from "@emotion/react";
import EditDialogBox from "./EditDialogBox";
import ApiKeyBox from "./ApiKeyBox";
import DeleteDialogBox from "./DeletDialogBox";
import { notification } from "antd";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AddIcon from "@mui/icons-material/Add";

export const APIkey = () => {
  const { user } = useSelector((state) => state.auth);
  const [apiKey, setApiKey] = useState(
    () => sessionStorage.getItem("apiKey") || user.apiKey
  );
  const [apiSecret, setApiSecret] = useState(
    () => sessionStorage.getItem("apiSecret") || user.apiSecret
  );
  const [showTable, setShowTable] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [editData, setEditData] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  /** Function to fetch API data */
  const fetchApiData = async () => {
  try {
    setLoading(true);
    const response = await keysPair({ id: user?.company?.id });

      const data = response.data.data.map((item, index) => {
        const currentApiKey = item.attributes.apiKey;
        const currentApiSecret = item.attributes.apiSecret;

        const shouldDisableActions =
          user?.company?.name === 'HabileLabs' &&
          currentApiKey === '4111598715' &&
          currentApiSecret === '8750c397cb1907d8a7d7f319b7e2d740e33810593fd4e16232d033d2076bb18e';

        return {
          id: item.id,
          serialNo: index + 1,
          name: item.attributes.apiAccessKeysName,
          api: item.attributes.apis.data.map((api) => api.attributes.name).join(", "),
          apiKey: (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
              {item.attributes.apiKey.length > 10
                ? item.attributes.apiKey.substring(0, 10) + "..."
                : item.attributes.apiKey}
              <Tooltip title="Copy" componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
                <IconButton onClick={() => handleCopy(item.attributes.apiKey, 'API Key')} sx={{ cursor: "pointer", ml: 1 }}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </div>
          ),
          apiSecret: (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
              <Tooltip title={item.attributes.apiSecret} placement="top">
                <span>
                  {item.attributes.apiSecret.length > 10
                    ? item.attributes.apiSecret.substring(0, 10) + "..."
                    : item.attributes.apiSecret}
                </span>
              </Tooltip>
              <Tooltip title="Copy" componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
                <IconButton onClick={() => handleCopy(item.attributes.apiSecret, 'API Secret')} sx={{ cursor: "pointer", ml: 1 }}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </div>
          ),
          action: (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", ...(shouldDisableActions ? { opacity: 0.5, pointerEvents: "none" } : {}), }}>
              <Tooltip title="Regenerate" componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
                <IconButton onClick={() => handleRegenerate(item.id)} sx={{ ml: 1, color: "#1976D2" }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit" componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
                <IconButton onClick={() => handleEdit(item)} sx={{ cursor: "pointer", ml: 1, color: "#1976D2" }}>
                  <EditOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
                <IconButton onClick={() => openDeleteDialog(item.id)} sx={{ cursor: "pointer", ml: 1, color: "#F18282" }}>
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </Tooltip>
            </div>
          ),
        };
      });
      setShowTable(data.length > 0);
      setApiData(data);
    } catch (error) {
      console.error("Error fetching API data:", error);
    } finally {
      setLoading(false);
    }
  };

  /** Effect to fetch API data on component mount */
  useEffect(() => {
    fetchApiData();
  }, []);

  /** Effect to update session storage for apiKey and apiSecret */
  useEffect(() => {
    sessionStorage.setItem("apiKey", apiKey);
    sessionStorage.setItem("apiSecret", apiSecret);
  }, [apiKey, apiSecret]);

  /** Function to handle API key regeneration */
  const handleRegenerate = async (id) => {
    try {
      setLoading(true);
      const res = await regenerateKeys(id);
      if (res?.data) {
        const { apiKey: newApiKey, apiSecret: newApiSecret } = res.data.data;
        /** Update the apiData state with the new API key and secret */
        setApiData((prevData) =>
          prevData.map((item) =>
            item.id === id
              ? {
                  ...item,
                  apiKey: (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
                      {newApiKey.length > 10 ? newApiKey.substring(0, 10) + '...' : newApiKey}
                      <Tooltip title="Copy" componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
                        <IconButton
                          onClick={() => handleCopy(newApiKey, 'API Key')}
                          sx={{ cursor: "pointer", ml: 1 }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ),
                  apiSecret: (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
                      <Tooltip title={newApiSecret} placement="top">
                        <span>{newApiSecret.length > 10 ? newApiSecret.substring(0, 10) + '...' : newApiSecret}</span>
                      </Tooltip>
                      <Tooltip title="Copy" componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
                        <IconButton
                          onClick={() => handleCopy(newApiSecret, 'API Secret')}
                          sx={{ cursor: "pointer", ml: 1 }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ),
                }
              : item
          )
        );
        notification.success({
          message: "Success",
          description: "API key regenerated successfully.",
        });
      } else {
        notification.error({
          message: "Error",
          description: "Failed to regenerate API key.",
        });
      }
    } catch (error) {
      console.error('Error regenerating API key and secret:', error);
      notification.error({
        message: "Error",
        description: "Something went wrong while regenerating.",
      });
    } finally {
      setLoading(false);
    }
  };

  /** Function to copy API key or API secret to clipboard */
  const handleCopy = (text, type = 'API Key') => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        notification.success({
          message: "Copied!",
          description: `${type} copied to clipboard successfully.`,
          duration: 2,
        });
      })
      .catch((error) => {
        console.error("Error copying text to clipboard:", error);
        notification.error({
          message: "Error",
          description: "Failed to copy to clipboard.",
        });
      });
  };

  const handleEdit = (item) => {
    setEditData(item);
    setIsEditDialogOpen(true);
  };
  
  const handleCreate = (item) => {
    if (apiData.length >= 10) {
      notification.warning({
        message: "Alert!",
        description: "You have reached the maximum 10 API key pair limit.",
      });
      return;
    }
    setEditData(item);
    setIsEditDialogOpen(true);
  };

  /** Function to open delete confirmation dialog */
  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  /** Function to confirm deletion */
  const confirmDelete = async () => {
    try {
      setLoading(true);
      await deleteKeysPair(deleteId);
      await fetchApiData();
      setDeleteDialogOpen(false);
      notification.success({
        message: "Deleted",
        description: "API key deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting API key:", error);
      notification.error({
        message: "Error",
        description: "Failed to delete API key.",
      });
    } finally {
      setDeleteDialogOpen(false);
      setLoading(false);
    }
  };

  /** Function to close delete dialog */
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  /** Function to close edit dialog */
  const handleClose = () => {
    setIsEditDialogOpen(false);
  };

  /** Function to save changes in edit dialog */
  const handleSave = async () => {
    try {
      setLoading(true); 
      await fetchApiData();
    } finally {
      setIsEditDialogOpen(false);
      setLoading(false);
    }
  };
  
  return (
    <Box>
      <Wrapper sx={{ paddingTop: theme.spacing(2) }}>
       {loading && <Loader />}
        {!showTable && <ApiKeyBox onSave={handleSave} />}
        {showTable && (
          <Box sx={{ mt: 10, width: {
            xs: "95%",  // phones
            sm: "100%",  // tablets (iPad portrait)
            md: "92%",  // iPad landscape / small laptop
            lg: "93%",  // desktops
            xl: "100%",  // very large screens
          }, }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#65758B",
                      mb: 3,
                      fontWeight: "500",
                    }}
                  >
                    Manage your API keys and authentication credentials
                  </Typography>
                </Box>
                <Button
                  onClick={() => {
                    handleCreate(null);
                  }}
                  variant="contained"
                >
                  <AddIcon />
                  Generate New Key
                </Button>
              </Box>
            </Box>

            <Stack spacing={1} sx={{ overflowX: "hidden" }}>
              <CustomTable
                customClass={false}
                columns={DataColumns}
                data={apiData}
              />
            </Stack>

            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                padding: 2,
                backgroundColor: "#fafafa",
                mt: 3,
              }}
            >
              <Typography
                sx={{
                  fontWeight: "700",
                  fontSize: "16px",
                  mb: 0.5,
                  color: "#020817",
                }}
              >
                Authentication Guidelines
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  mb: 1,
                  fontWeight: "500",
                  color: "#65758B",
                }}
              >
                Important security practices for using your API keys
              </Typography>

              <List sx={{ py: 0 }}>
                {[
                  "Include your API key in the Authorization header: Bearer YOUR_API_KEY",
                  "Never expose your API keys in client-side code or public repositories",
                  "Regenerate keys immediately if you suspect they have been compromised",
                ].map((text, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      py: 0.5,
                      px: 0,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <FiberManualRecordIcon
                        sx={{ fontSize: 6, color: "#1976d2" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: "14px",
                        color: "#333",
                      }}
                      primary={text}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        )}
        {isEditDialogOpen && (
          <EditDialogBox
            open={isEditDialogOpen}
            handleClose={handleClose}
            handleSave={handleSave}
            editData={editData}
          />
        )}
        <DeleteDialogBox
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          onConfirm={confirmDelete}
        />
      </Wrapper>
    </Box>
  );
}
export default APIkey;
